import Head from "next/head";
import React from "react";
import Link from "next/link";
import { ExtendedNextPage } from "types/pages";
import { ParsedUrlQuery } from "querystring";

import HummingNav from "pages/components/HummingNav";
import HummingFooter from "pages/components/HummingFooter";
import Label from "components/Label";
import Content from "components/Content";

import styles from "./styles.module.scss";
import { getLocation, getLocations, getServices } from "utils/content/services";
import { GetStaticPaths, GetStaticProps } from "next";
import { HowItWorks, YourMembership } from "pages/homepage/components";
import TheDifference from "pages/homepage/components/TheDifference";
import Header from "pages/blog/components/Header";
import { LocationData } from "../index.page";

export type Service = {
  title: string;
  slug: string;
};

const ServicesLocationsPage: ExtendedNextPage<{
  servicesData: Service[];
  locationTitle: string;
  locationSlug: string;
}> = ({ servicesData, locationTitle, locationSlug }) => {
  return (
    <div className={styles.container}>
      <Head>
        <title>{`Services Offered Through Humming Homes in ${locationTitle}`}</title>
        <meta
          name="description"
          content="Your source for all things tied to your home. From property management advice to proactive maintenance to city guides, we've got you covered."
        />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Header
        title={
          locationTitle
            ? `Services Offered Through Humming Homes in ${locationTitle}`
            : "Services we offer"
        }
      />
      <Content
        contentClassName={styles.locationsContainer}
        containerClassName={styles.locationsWrapper}
      >
        <aside className={styles.services}>
          <div className={styles.servicesInner}>
            <ul className={styles.servicesList}>
              {servicesData.map((service) => (
                <li key={service.slug}>
                  <Link href={`/services/${locationSlug}/${service.slug}`}>
                    <Label className={styles.service} variant="s" renderNode>
                      <img
                        src={`/services/icons/${service.slug}.svg`}
                        alt={`${service.title} icon`}
                      />
                      <span className={styles.otherServiceTitle}>
                        {service.title}
                      </span>
                    </Label>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </Content>

      <HowItWorks />
      <YourMembership />
      <TheDifference />
    </div>
  );
};

interface Params extends ParsedUrlQuery {
  locationSlug: string;
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { locationSlug } = params as Params;
  const { items } = await getLocation(locationSlug);
  const services = await getServices();
  // console.log(services, "services!");
  const servicesData = services.items.map(
    (location: LocationData) => location.fields
  );
  return {
    props: {
      servicesData,
      locationTitle: items[0]?.fields?.title,
      locationSlug: items[0]?.fields?.slug
    }
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { items } = await getLocations();
  return {
    paths: items.map((location: LocationData) => {
      return { params: { locationSlug: location.fields.slug } };
    }),
    fallback: false
  };
};

ServicesLocationsPage.getLayout = (page) => {
  return (
    <div>
      <HummingNav />
      {page}
      <HummingFooter />
    </div>
  );
};

export default ServicesLocationsPage;
